import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

import { buildClassName } from '@utils';

import './Modal.css';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close (backdrop click, close button, Escape) */
  onClose: () => void;
  /** Modal title (required for accessibility) */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Modal content */
  children: React.ReactNode;
  /** Id for the title element (for aria-labelledby). Defaults to auto-generated. */
  titleId?: string;
  /** Id for the description element (for aria-describedby). Optional. */
  descriptionId?: string;
  /** Accessible label for the close button */
  closeButtonLabel?: string;
  /** When true, the header close (X) button is not rendered. Use when the user must choose an explicit action. */
  hideCloseButton?: boolean;
  /** When false, clicking the backdrop does not close the modal. Default true. */
  closeOnBackdropClick?: boolean;
  /** When false, pressing Escape does not close the modal. Default true. */
  closeOnEscape?: boolean;
  /** Optional content to render in the header (e.g. icon) */
  headerContent?: React.ReactNode;
  /** Max width of the dialog (CSS value). Default 420px. */
  maxWidth?: string;
  /** Additional class for the dialog element */
  className?: string;
}

/**
 * Reusable modal dialog. Uses design tokens for backdrop, shadow, and colors.
 * Locks body scroll when open, closes on Escape, and supports optional focus trap.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  titleId: titleIdProp,
  descriptionId: descriptionIdProp,
  closeButtonLabel = 'Close',
  hideCloseButton = false,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  headerContent,
  maxWidth = '420px',
  className = '',
}: ModalProps) {
  const generatedId = useId();
  const titleId = titleIdProp ?? `bhg-modal-title-${generatedId}`;
  const descriptionId =
    descriptionIdProp ?? (description ? `bhg-modal-desc-${generatedId}` : undefined);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const dialogClasses = buildClassName(
    'bhg-modal__dialog',
    className
  );

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) onClose();
  };

  return createPortal(
    <div
      className="bhg-modal__backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y-x/click-events-have-key-events, jsx-a11y-x/no-noninteractive-element-interactions -- propagation guard only */}
      <div
        className={dialogClasses}
        style={{ maxWidth }}
        // Stops clicks inside the dialog from reaching the backdrop and, through the portal,
        // any clickable React ancestor the modal is rendered under. Not a user interaction.
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId ?? undefined}
      >
        <div className="bhg-modal__header">
          {headerContent != null && (
            <div className="bhg-modal__header-content">{headerContent}</div>
          )}
          <h2 id={titleId} className="bhg-modal__title">
            {title}
          </h2>
          {description != null && (
            <p id={descriptionId} className="bhg-modal__description">
              {description}
            </p>
          )}
          {!hideCloseButton && (
            <button
              type="button"
              className="bhg-modal__close"
              onClick={onClose}
              aria-label={closeButtonLabel}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>
        <div className="bhg-modal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
}
